import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Save, Lock, User, Building2, Globe, CreditCard, Sun } from 'lucide-react';
import PartnerLayout from '../../components/PartnerLayout';
import ThemeToggle from '../../components/ThemeToggle';
import { partnerPortalAPI } from '../../lib/partnerApi';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';

const PartnerSettingsPage = () => {
  const [partner, setPartner] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Profile form
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [phone, setPhone] = useState('');
  const [country, setCountry] = useState('');
  const [website, setWebsite] = useState('');

  // Bank form
  const [bankName, setBankName] = useState('');
  const [accountHolder, setAccountHolder] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [iban, setIban] = useState('');
  const [swift, setSwift] = useState('');
  const [paypalEmail, setPaypalEmail] = useState('');

  // Password form
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');

  useEffect(() => {
    partnerPortalAPI.getSettings()
      .then(res => {
        const p = res.data.partner;
        setPartner(p);
        setName(p.partner_name || '');
        setCompany(p.company_name || '');
        setPhone(p.phone || '');
        setCountry(p.country || '');
        setWebsite(p.website || '');
        const bd = p.bank_details || {};
        setBankName(bd.bank_name || '');
        setAccountHolder(bd.account_holder || '');
        setAccountNumber(bd.account_number || '');
        setIban(bd.iban || '');
        setSwift(bd.swift_code || '');
        setPaypalEmail(bd.paypal_email || '');
      })
      .catch(() => toast.error('Failed to load settings'))
      .finally(() => setLoading(false));
  }, []);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await partnerPortalAPI.updateSettings({
        partner_name: name,
        company_name: company,
        phone,
        country,
        website,
      });
      toast.success('Profile updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const saveBankDetails = async () => {
    setSaving(true);
    try {
      await partnerPortalAPI.updateSettings({
        bank_details: {
          bank_name: bankName,
          account_holder: accountHolder,
          account_number: accountNumber,
          iban,
          swift_code: swift,
          paypal_email: paypalEmail,
        },
      });
      toast.success('Bank details updated');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const savePassword = async () => {
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    if (newPw.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSaving(true);
    try {
      await partnerPortalAPI.changePassword({ current_password: currentPw, new_password: newPw });
      toast.success('Password updated');
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update password');
    } finally { setSaving(false); }
  };

  if (loading) return (
    <PartnerLayout>
      <div className="flex items-center justify-center h-64 text-muted-foreground">Loading…</div>
    </PartnerLayout>
  );

  return (
    <PartnerLayout>
      <div data-testid="partner-settings" className="space-y-6 pb-6 max-w-2xl">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Manage your profile, bank details, and password</p>
        </div>

        {/* Read-only info */}
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-emerald-700 font-medium">Referral Code</p>
            <p className="text-lg font-bold text-emerald-800 tracking-widest">{partner?.referral_code}</p>
          </div>
          <div>
            <p className="text-xs text-emerald-700 font-medium">Commission Rates</p>
            <p className="text-sm font-semibold text-emerald-800">Premium: {partner?.premium_commission_percent}% · AI: {partner?.ai_commission_percent}%</p>
          </div>
        </div>

        <Tabs defaultValue="profile">
          <TabsList className="grid grid-cols-4 w-full">
            <TabsTrigger value="profile"><User className="w-4 h-4 mr-2" />Profile</TabsTrigger>
            <TabsTrigger value="bank"><CreditCard className="w-4 h-4 mr-2" />Bank Details</TabsTrigger>
            <TabsTrigger value="password"><Lock className="w-4 h-4 mr-2" />Password</TabsTrigger>
            <TabsTrigger value="appearance" data-testid="tab-appearance"><Sun className="w-4 h-4 mr-2" />Appearance</TabsTrigger>
          </TabsList>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Full Name</Label>
                  <Input value={name} onChange={e => setName(e.target.value)} className="mt-1.5" data-testid="settings-name" />
                </div>
                <div>
                  <Label>Company Name</Label>
                  <Input value={company} onChange={e => setCompany(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={phone} onChange={e => setPhone(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>Country</Label>
                  <Input value={country} onChange={e => setCountry(e.target.value)} className="mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Website</Label>
                <Input value={website} onChange={e => setWebsite(e.target.value)} placeholder="https://…" className="mt-1.5" />
              </div>
              <div>
                <Label>Email</Label>
                <Input value={partner?.email || ''} disabled className="mt-1.5 bg-muted" />
                <p className="text-xs text-muted-foreground mt-1">Email cannot be changed</p>
              </div>
              <Button onClick={saveProfile} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-profile-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Profile'}
              </Button>
            </div>
          </TabsContent>

          {/* Bank Details Tab */}
          <TabsContent value="bank">
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
              <p className="text-sm text-muted-foreground">These details will be used for your withdrawal requests.</p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Bank Name</Label>
                  <Input value={bankName} onChange={e => setBankName(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>Account Holder</Label>
                  <Input value={accountHolder} onChange={e => setAccountHolder(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>Account Number</Label>
                  <Input value={accountNumber} onChange={e => setAccountNumber(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>IBAN</Label>
                  <Input value={iban} onChange={e => setIban(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>SWIFT / BIC Code</Label>
                  <Input value={swift} onChange={e => setSwift(e.target.value)} className="mt-1.5" />
                </div>
                <div>
                  <Label>PayPal Email</Label>
                  <Input value={paypalEmail} onChange={e => setPaypalEmail(e.target.value)} className="mt-1.5" />
                </div>
              </div>
              <Button onClick={saveBankDetails} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-bank-btn">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Bank Details'}
              </Button>
            </div>
          </TabsContent>

          {/* Password Tab */}
          <TabsContent value="password">
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4">
              <div>
                <Label>Current Password</Label>
                <Input type="password" value={currentPw} onChange={e => setCurrentPw(e.target.value)} className="mt-1.5" data-testid="current-password" />
              </div>
              <div>
                <Label>New Password</Label>
                <Input type="password" value={newPw} onChange={e => setNewPw(e.target.value)} className="mt-1.5" data-testid="new-password" />
              </div>
              <div>
                <Label>Confirm New Password</Label>
                <Input type="password" value={confirmPw} onChange={e => setConfirmPw(e.target.value)} className="mt-1.5" data-testid="confirm-password" />
              </div>
              <Button onClick={savePassword} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="save-password-btn">
                <Lock className="w-4 h-4 mr-2" /> {saving ? 'Updating…' : 'Update Password'}
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="appearance">
            <div className="bg-card rounded-xl border border-border p-6 shadow-sm space-y-4" data-testid="appearance-tab">
              <div>
                <h3 className="font-medium text-foreground">Appearance</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Choose light, dark, or match your system preference.
                </p>
              </div>
              <ThemeToggle showLabels />
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </PartnerLayout>
  );
};

export default PartnerSettingsPage;
